'use client';

import { Box, CircularProgress, Stack, Step, StepLabel, Stepper, Theme } from '@mui/material';
import tokens from '@verifime/design-tokens';
import { Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react';

export type TStepNumber = number | 'first' | 'last' | 'both';

/**
 * What the confirmation step does once the individual is submitted: open the organisation set-up
 * (`SETUP_ORGANISATION`), or show the end screen (`SHOW_COMPLETION`).
 */
export type TConfirmationAction = 'SETUP_ORGANISATION' | 'SHOW_COMPLETION';

/**
 * What wizard steps hand each other. Open-ended (`Record<string, any>`); the fields named here are
 * typed so a rename on either side fails the build instead of silently dropping the hand-off.
 */
export type TMemoizedObj = Record<string, any> & {
  /**
   * Entity Selection First (VME-4142): the entity an ask-first journey already chose, handed from
   * the individual consents step to the confirmation step, which opens its wizard.
   */
  chosenEntityType?: string;
  /**
   * The id of the Draft entity the customer chose on the confirm screen, handed on with
   * `chosenEntityType` so the confirmation step resumes that Draft's wizard instead of a blank one.
   */
  chosenEntityId?: string;
  /**
   * What the confirmation step does next, set by the consents step. `null` on the masquerade
   * branch: an admin submitting on a customer's behalf is shown the masquerade notice instead, and
   * no action follows. Absent when no step has set one, which the confirmation step treats as
   * neither action and answers with a Back to Home button.
   */
  confirmationAction?: TConfirmationAction | null;
};

export type TStepContent = {
  /**
   * Step is 0-based number or one of 'first', 'last' and 'both'
   * step === 'frist' means this is the first steap
   * step === 'last' means this is the last step
   * step === 'both' means the step is the first and last, normally it happens when there is only one step
   */
  step: TStepNumber;
  numSteps: number;
  stepIndex?: number; // 0-based index of the step
  memoizedObj?: TMemoizedObj;
  setMemoizedObj?: Dispatch<SetStateAction<TMemoizedObj>>;
  onBack?: () => void;
  onNext?: () => void;
};

export type TStep = {
  content: (props: TStepContent) => ReactNode;
};

export type TSteps = TStep[];

export type TStepHeader = ({
  currentStep,
  memoizedObj,
  setMemoizedObj,
}: {
  currentStep: TStepNumber;
  memoizedObj: Record<string, any>;
  setMemoizedObj: Dispatch<SetStateAction<{}>>;
}) => ReactNode;

export default function StepsWizard({
  steps,
  defaultActiveStep = 0,
  header,
  isShowStepConnector = true,
  isShowStepLabels = true,
  prepare,
  dataTestId,
  stepperHeader,
}: Readonly<{
  steps: TSteps | ((props: { memoizedObj: Record<string, any> }) => Promise<TSteps>);
  defaultActiveStep?: number;
  header?: ({
    currentStep,
    memoizedObj,
    setMemoizedObj,
  }: {
    currentStep: TStepNumber;
    memoizedObj: Record<string, any>;
    setMemoizedObj: Dispatch<SetStateAction<{}>>;
  }) => ReactNode;
  isShowStepConnector?: boolean;
  isShowStepLabels?: boolean;
  prepare?: ({ setMemoizedObj }: { setMemoizedObj: Dispatch<SetStateAction<{}>> }) => void;
  dataTestId?: string;
  stepperHeader?:
    | ReactNode
    | ((props: { currentStep: TStepNumber; memoizedObj: Record<string, any> }) => ReactNode);
}>) {
  const [memoizedObj, setMemoizedObj] = useState({});
  const [currentSteps, setCurrentSteps] = useState<TSteps>([]);
  const [activeStep, setActiveStep] = useState(defaultActiveStep);
  const [isPreparing, setIsPreparing] = useState(true);

  useEffect(() => {
    const initializeSteps = async () => {
      const resolvedSteps = typeof steps === 'function' ? await steps({ memoizedObj }) : steps;
      setCurrentSteps(resolvedSteps);
      setActiveStep((prev) => Math.max(Math.min(prev, resolvedSteps.length - 1), 0));
    };
    initializeSteps();
  }, [steps, memoizedObj]);

  useEffect(() => {
    if (typeof prepare !== 'function') {
      setIsPreparing(false);
      return;
    }
    prepare?.({ setMemoizedObj });
    setIsPreparing(false);
  }, [prepare]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => {
      const nextStep = prevActiveStep + 1;
      return Math.min(nextStep, currentSteps.length - 1);
    });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => {
      const prevStep = prevActiveStep - 1;
      return Math.max(prevStep, 0);
    });
  };

  if (currentSteps?.length < 1) {
    return null;
  }

  if (!currentSteps?.[activeStep]) {
    return null;
  }

  if (isPreparing) {
    return <CircularProgress />;
  }

  const activeStepContent = currentSteps[activeStep].content;

  return (
    <Box sx={{ width: '100%', height: '100%' }} data-testid={dataTestId}>
      {header?.({
        currentStep: getCurrentStep(activeStep, currentSteps.length),
        memoizedObj,
        setMemoizedObj,
      })}

      <Stack direction="column" alignItems="center" justifyContent="center">
        {typeof stepperHeader === 'function'
          ? stepperHeader({
              currentStep: getCurrentStep(activeStep, currentSteps.length),
              memoizedObj,
            })
          : stepperHeader}
        {isShowStepConnector && (
          <Stepper
            activeStep={activeStep}
            sx={{
              width: '100%',
              maxWidth: [tokens.sizeSm, tokens.sizeSm, tokens.sizeSm, tokens.sizeSm, tokens.sizeMd],
              paddingTop: tokens.spacingBase,
            }}
            data-testid={`${dataTestId}-stepper`}
          >
            {currentSteps.map((_, index) => {
              return (
                <Step data-testid={`${dataTestId}-stepper-${index + 1}`} key={index}>
                  {isShowStepLabels && <StepLabel />}
                </Step>
              );
            })}
          </Stepper>
        )}
      </Stack>

      <Box
        sx={{
          maxWidth: [tokens.sizeSm, tokens.sizeSm, tokens.sizeSm, tokens.sizeSm, tokens.sizeMd],
          margin: 'auto',
          height: '100%',
        }}
        data-testid={`${dataTestId}-step-${activeStep + 1}-content`}
      >
        {activeStepContent({
          onBack: handleBack,
          onNext: handleNext,
          step: getCurrentStep(activeStep, currentSteps.length),
          stepIndex: activeStep,
          numSteps: currentSteps.length,
          memoizedObj,
          setMemoizedObj,
        })}
      </Box>
    </Box>
  );
}

function getCurrentStep(activeStep: number, lenSteps: number) {
  if (lenSteps === 1) {
    return 'both';
  }
  if (activeStep === 0) {
    return 'first';
  }
  if (activeStep === lenSteps - 1) {
    return 'last';
  }
  return activeStep;
}
